<script setup lang="ts">
/**
 * A dropdown menu rendered in <body> with `position: fixed`, so a card's or table's `overflow-hidden`
 * can't clip it (gap analysis §14 #2: content card ⋮ and placement row ⋮ lost their last items).
 *
 * - Placed under its anchor, right-aligned; flips above when there is no room below; clamped to the viewport.
 * - `marker` is the attribute the parent's outside-click handler looks for (`data-menu`, `data-row-menu`…).
 * - Closes on page scroll or resize: a fixed menu would otherwise drift away from its anchor.
 * - Closes by itself on a click/tap anywhere outside it (or its anchor) and on Esc; the parent owns `open`.
 * - Opens with a short fade-and-scale from the side it's attached to (none with reduced motion).
 */
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    open: boolean;
    anchor: HTMLElement | null;
    width?: string;
    marker?: string;
    /** `end`: right edges aligned (⋮ buttons); `start`: opens from the anchor's left (right-click at the cursor). */
    align?: 'start' | 'end';
  }>(),
  { width: 'w-44', marker: 'data-menu', align: 'end' },
);
const emit = defineEmits<{ close: [] }>();

const panel = ref<HTMLElement | null>(null);
const pos = ref<{ top: number; left: number } | null>(null);
/** Where the open animation grows from: the corner nearest the anchor. */
const origin = ref('top right');
const GAP = 4;
const EDGE = 8;

async function place(): Promise<void> {
  pos.value = null;
  await nextTick();
  if (!props.anchor || !panel.value) return;
  const r = props.anchor.getBoundingClientRect();
  const { offsetWidth: w, offsetHeight: h } = panel.value;
  const roomBelow = window.innerHeight - r.bottom - GAP - EDGE;
  const up = h > roomBelow && r.top - GAP - EDGE > roomBelow;
  const top = up ? Math.max(EDGE, r.top - GAP - h) : r.bottom + GAP;
  const left = Math.min(Math.max(EDGE, props.align === 'start' ? r.left : r.right - w), window.innerWidth - w - EDGE);
  pos.value = { top, left };
  origin.value = `${up ? 'bottom' : 'top'} ${props.align === 'start' ? 'left' : 'right'}`;
}

watch(
  () => [props.open, props.anchor] as const,
  ([open]) => {
    if (open) void place();
  },
);

const onViewportChange = (e: Event) => {
  if (!props.open) return;
  // Scrolling inside the menu itself is fine; anything else moves the anchor.
  if (e.type === 'scroll' && panel.value?.contains(e.target as Node)) return;
  emit('close');
};
/** A press anywhere else closes it (the anchor toggles it itself, so it's left alone). */
const onPointerDown = (e: PointerEvent) => {
  if (!props.open) return;
  const target = e.target as Node;
  if (panel.value?.contains(target) || props.anchor?.contains(target)) return;
  emit('close');
};
const onKey = (e: KeyboardEvent) => {
  if (props.open && e.key === 'Escape') {
    emit('close');
    props.anchor?.focus();
  }
};
onMounted(() => {
  window.addEventListener('scroll', onViewportChange, true);
  window.addEventListener('resize', onViewportChange);
  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
  window.removeEventListener('scroll', onViewportChange, true);
  window.removeEventListener('resize', onViewportChange);
  document.removeEventListener('pointerdown', onPointerDown, true);
  document.removeEventListener('keydown', onKey);
});
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-150 ease-out motion-reduce:transition-none"
      enter-from-class="opacity-0 scale-95 -translate-y-1"
      leave-active-class="transition duration-100 ease-in motion-reduce:transition-none"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="open"
        ref="panel"
        role="menu"
        v-bind="{ [marker]: '' }"
        class="fixed z-50 max-h-[70vh] overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 text-start text-sm shadow-xl shadow-slate-900/15 ring-1 ring-black/5 dark:border-slate-700 dark:bg-slate-900 dark:shadow-black/50 dark:ring-white/5"
        :class="width"
        :style="pos ? { top: `${pos.top}px`, left: `${pos.left}px`, transformOrigin: origin } : { top: '0px', left: '0px', visibility: 'hidden' }"
        @click.stop
      >
        <slot />
      </div>
    </Transition>
  </Teleport>
</template>
